import { atom, useRecoilState, useResetRecoilState } from "recoil";
import { useCallback } from "react";
import { randomString } from "../../Functions";
import { QuestionnairesState, UseQuestionnairesReturn } from "./type";
import * as topikAPI from "../../Data/Topik";

// Questionnaires state atom
export const questionnairesState = atom<QuestionnairesState>({
    key: "questionnairesState" + randomString(4),
    default: {
        items: {},
        examContent: {},
        loading: false,
        error: null
    }
});

export const useQuestionnaires = (): UseQuestionnairesReturn => {
    const [state, setState] = useRecoilState(questionnairesState);
    const resetQuestionnaires = useResetRecoilState(questionnairesState);

    // Get questionnaire with full exam content
    const getQuestionnaire = useCallback(async (params: { 
        examid?: string; 
        questionnaireid?: string;
        section?: 'listening' | 'reading' | 'writing';
    }): Promise<topikAPI.QuestionnaireResponse | null> => {
        const cacheKey = `${params.questionnaireid || params.examid || ''}_${params.section || 'all'}`;
        
        // Return cached content if available
        if (state.examContent[cacheKey]) {
            return state.examContent[cacheKey];
        }

        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const content = await topikAPI.getQuestionnaire(params);
            
            if (content) {
                setState(prev => ({
                    ...prev,
                    examContent: { ...prev.examContent, [cacheKey]: content },
                    loading: false
                }));
                
                return content;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return null;
        } catch (error) {
            console.error('Failed to get questionnaire:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to load questionnaire' 
            }));
            return null;
        }
    }, [state.examContent, setState]);

    // List user's questionnaires
    const syncQuestionnaires = useCallback(async (params?: { 
        userid?: string;
        status?: string;
        limit?: number;
        offset?: number;
    }): Promise<topikAPI.QuestionnaireType[]> => {
        setState(prev => ({ ...prev, loading: true, error: null }));
        
        try {
            const questionnaires = await topikAPI.listQuestionnaires(params);
            
            if (questionnaires) {
                const items = Object.fromEntries(
                    questionnaires.map(q => [q.questionnaireid, q])
                );
                
                setState(prev => ({
                    ...prev,
                    items: { ...prev.items, ...items },
                    loading: false
                }));
                
                return questionnaires;
            }
            
            setState(prev => ({ ...prev, loading: false }));
            return [];
        } catch (error) {
            console.error('Failed to list questionnaires:', error);
            setState(prev => ({ 
                ...prev, 
                loading: false, 
                error: 'Failed to load questionnaires' 
            }));
            return [];
        }
    }, [setState]);

    const reset = useCallback(() => {
        resetQuestionnaires();
    }, [resetQuestionnaires]);

    return {
        questionnaires: state.items,
        examContent: state.examContent,
        loading: state.loading,
        error: state.error,
        getQuestionnaire,
        syncQuestionnaires,
        reset
    };
};